import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { realtimeConfig } from './config.ts';
import { MqttTransport } from './mqttTransport.ts';
import type { RealtimeMessage } from './transport.ts';

// The "events invalidate queries" contract (PRD-0006 D10, SPA seam #1),
// transport-agnostic. This is the exact contract the SSE hooks had — subscribe
// to a live channel and, on each pushed event, invalidate a set of query keys so
// TanStack Query refetches the REST source of truth — but the wire is now MQTT-
// over-WebSocket to AWS IoT Core instead of SSE.
//
// Invalidation alone loses the race for robot-originated STATUS events: the
// browser hears the robot's MQTT publish near-instantly, but the DB row it
// refetches is written by the slower ingest path (IoT rule → ingest Lambda →
// API → DB), so the refetch reads the OLD status and nothing pushes again until
// the command's next transition. Callers whose payloads carry enough state can
// therefore pass `applyToCache` to patch the query cache directly; the keysFor
// invalidation still runs, just DELAYED (`reconcileDelayMs`), as a reconcile
// against the REST source of truth once ingest has landed. REST stays
// authoritative; the patch is only ever a forward-looking preview.
//
// Env-gated: when the IoT/Cognito config is absent (local dev / pre-IoT / IAP
// deployment), `enabled` is false and this no-ops — callers keep their existing
// polling with zero regression.

export interface RealtimeInvalidation {
  // MQTT topic filters to subscribe to, all under the caller's own tenant so the
  // authorizer's tenant-scoped policy permits them (e.g. `fleet/{tenant}/#`).
  topicFilters: string[];
  // Given a received message, the query keys to invalidate. Returning [] ignores
  // the message (e.g. a topic the caller doesn't care about).
  keysFor: (msg: RealtimeMessage) => QueryKey[];
  // Optional optimistic cache patch. Return true when the message was applied
  // to the cache; the keysFor invalidation is then deferred by reconcileDelayMs
  // instead of firing immediately (see the race note above). Return false to
  // fall back to plain immediate invalidation for that message.
  applyToCache?: (qc: QueryClient, msg: RealtimeMessage) => boolean;
  // Delay for the reconcile invalidation after a successful cache patch.
  reconcileDelayMs?: number;
  // When false the hook does nothing (e.g. no robotId yet). Defaults to true.
  enabled?: boolean;
}

export function useRealtimeInvalidation(
  spec: RealtimeInvalidation,
): { connected: boolean; active: boolean } {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  const { topicFilters, keysFor, applyToCache, reconcileDelayMs = 2000,
          enabled = true } = spec;
  // Stable key so the effect re-subscribes only when the filter set changes.
  const filtersKey = topicFilters.join('\n');

  useEffect(() => {
    if (!enabled || topicFilters.length === 0) return;
    const cfg = realtimeConfig();
    if (!cfg) return; // realtime disabled → caller's polling covers it

    const timers = new Set<number>();
    const transport = new MqttTransport(cfg);
    const close = transport.subscribe(topicFilters, {
      onConnectedChange: setConnected,
      onMessage: (msg) => {
        const invalidate = () => {
          for (const key of keysFor(msg)) {
            void qc.invalidateQueries({ queryKey: key });
          }
        };
        if (applyToCache?.(qc, msg)) {
          // Cache already shows the transition; reconcile with REST once the
          // ingest write has had time to land.
          const t = window.setTimeout(() => {
            timers.delete(t);
            invalidate();
          }, reconcileDelayMs);
          timers.add(t);
        } else {
          invalidate();
        }
      },
    });

    return () => {
      close();
      for (const t of timers) window.clearTimeout(t);
      setConnected(false);
    };
    // keysFor/applyToCache are intentionally excluded: callers pass inline
    // closures, and the filter set + enabled flag are the real subscription
    // identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, enabled, qc, reconcileDelayMs]);

  // `active` tells callers whether realtime took over (so they can slow/stop
  // polling); false means the env is unconfigured and polling must stay.
  return { connected, active: enabled && realtimeConfig() !== null };
}
